import { useState, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useAuth0 } from '@auth0/auth0-react';
import { Box, Grid, Typography } from '@mui/material';
import { AddCircleOutline as AddIcon } from '@mui/icons-material';
import moment from 'moment';

import {
  FBoxContainer,
  FHButton,
  FHAntdDateTimePicker,
  FHInputField,
  FHTextarea,
  FProgressBar,
} from '../../../base';
import ToolRow from './ToolRow';
import { useTreatmentPlanEditable } from '../hooks/useTreatmentPlanEditable';
import { medicalHistoryActions } from '../../../../store';
import { ReferralHistorySchema, ToolRowSchema, initialReferralHistory } from './validationSchema';
import {
  validateSchema,
  validateSync,
  getAllInvalidFieldNames,
} from '../../../../helpers/validation-helpers';
import { MINIMUM_REQUIRED_SENTENCE_LENGTH, Themes } from '../../../../constants';
import { FORTA_ERROR, FORTA_BLUE, FORTA_WHITE } from '../../../../utils/colors';
import { errorStateConstants } from './medicalHistoryConstants';
import { debounce, disabledFutureDate } from '../../../../helpers';
import ErrorIcon from '../../../../assets/images/error-alert.svg';

import type { IReferralAssessmentTool } from '../../../../utils/types';
import type { Moment } from 'moment';
import type { ChangeEvent } from 'react';

function ReferralHistory() {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const editable = useTreatmentPlanEditable();
  const { current: treatmentPlan, loading: treatmentPlanLoading } = useSelector(
    (state: any) => state.treatmentPlans,
  );
  const { referralHistory, referralHistoryLoading, errorState } = useSelector(
    (state: any) => state.medicalHistories,
  );
  const [errors, setErrors] = useState<any>({});
  const [isAddingTool, setIsAddingTool] = useState<boolean>(false);
  const [toolsLength, setToolsLength] = useState<number>(0);

  const updateErrorCount = (errors: any) => {
    const errorCount = Object.values(errors).filter(Boolean).length;
    dispatch(
      medicalHistoryActions.updateErrorState({ [errorStateConstants.referralHistory]: errorCount }),
    );
  };

  const debouncedErrorUpdater = useCallback(debounce(updateErrorCount, 500), []);

  useEffect(() => {
    if (!treatmentPlan?.id) {
      return;
    }
    fetchReferralHistoryData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treatmentPlan]);

  useEffect(() => {
    if (!referralHistory || Object.keys(referralHistory).length === 0) {
      const errors = getAllInvalidFieldNames(ReferralHistorySchema, initialReferralHistory);
      setErrors(errors);
      return;
    }

    const { id: _id, ...rest } = referralHistory;
    Object.keys(rest).forEach((key: string) => {
      handleValidate(key, referralHistory);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralHistory]);

  useEffect(() => {
    if (!referralHistory?.assessment_tools) return;

    if (referralHistory.assessment_tools.length > toolsLength) {
      setIsAddingTool(false);
    }

    setToolsLength(referralHistory.assessment_tools?.length);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [referralHistory?.assessment_tools]);

  useEffect(() => {
    debouncedErrorUpdater(errors);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [errors]);

  const fetchReferralHistoryData = async () => {
    dispatch(
      medicalHistoryActions.getReferralHistory({
        getAccessTokenSilently,
        planId: treatmentPlan.id,
      }),
    );
  };

  const handleValidate = (key: string, data: any = referralHistory) => {
    if (!data) return;

    const isValid = validateSchema(ReferralHistorySchema, key, data);
    setErrors((prev: any) => ({ ...prev, [key]: !isValid }));
  };

  const handleFieldUpdate = (field: string, value: string) => {
    const updatedData = { ...referralHistory, [field]: value };
    dispatch(medicalHistoryActions.updateState({ key: 'referralHistory', data: updatedData }));
  };

  const handleCreateOrUpdateReferralHistory = async () => {
    const isValid = validateSync(ReferralHistorySchema, referralHistory);
    if (!isValid || !treatmentPlan?.id) return;

    if (referralHistory?.id) {
      dispatch(
        medicalHistoryActions.updateReferralHistory({
          getAccessTokenSilently,
          planId: treatmentPlan.id,
          data: referralHistory,
        }),
      );
    } else {
      dispatch(
        medicalHistoryActions.createReferralHistory({
          getAccessTokenSilently,
          planId: treatmentPlan.id,
          data: referralHistory,
        }),
      );
    }
  };

  const handleBlur = (key: string) => {
    handleValidate(key);
    handleCreateOrUpdateReferralHistory();
  };

  const handleAddTools = () => {
    setIsAddingTool(true);
  };

  const handleSaveTool = async (tool: IReferralAssessmentTool) => {
    const isValid = validateSync(ToolRowSchema, tool);
    if (!isValid || !treatmentPlan || !referralHistory) return;

    if (tool?.id) {
      dispatch(
        medicalHistoryActions.updateAssesmentTool({
          planId: treatmentPlan.id,
          referralHistoryId: referralHistory.id,
          toolId: tool?.id,
          data: tool,
          getAccessTokenSilently,
        }),
      );
    } else {
      dispatch(
        medicalHistoryActions.createAssesmentTool({
          planId: treatmentPlan.id,
          referralHistoryId: referralHistory.id,
          data: tool,
          getAccessTokenSilently,
        }),
      );
    }
  };

  const handleRemoveTool = async (id: number | undefined) => {
    if (!treatmentPlan || !referralHistory) return;

    if (id) {
      dispatch(
        medicalHistoryActions.deleteAssesmentTool({
          getAccessTokenSilently,
          planId: treatmentPlan.id,
          referralHistoryId: referralHistory.id,
          toolId: id,
        }),
      );
    } else {
      setIsAddingTool(false);
    }
  };

  if (!treatmentPlan?.id) {
    return <FBoxContainer colorTheme={Themes.Primary} title="Referral history" sx={{ mt: 3 }} />;
  }

  if (treatmentPlanLoading) {
    return <FProgressBar />;
  }

  return (
    <FBoxContainer colorTheme={Themes.Primary} title="Referral history" sx={{ mt: 3 }}>
      {editable && (
        <Typography
          textAlign={'left'}
          alignSelf="start"
          fontWeight={'700'}
          lineHeight={'21px'}
          marginLeft={'0px'}
          mb={3}
          sx={{
            color:
              errorState[errorStateConstants.referralHistory] > 0 ? FORTA_ERROR : FORTA_BLUE.light1,
          }}
        >
          {errorState[errorStateConstants.referralHistory] > 0 && (
            <img
              style={{ width: '20px', height: '20px', marginRight: '8px', marginTop: '-4px' }}
              src={ErrorIcon}
              alt="attention-icon"
            />
          )}
          Attention: All fields with * are required to be filled
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center' }} mb={2}>
        <Grid container spacing={2}>
          <Grid item xs={12} display={'flex'}>
            <Grid item xs={2} marginRight={3}>
              <Typography fontWeight="bold" mb={1}>
                Date of diagnosis*
              </Typography>
              <FHAntdDateTimePicker
                data-testid="DateOfDiagnosis"
                format="MM/DD/YY"
                allowClear={false}
                disabledDate={disabledFutureDate}
                value={
                  referralHistory?.date_of_diagnosis
                    ? moment(referralHistory.date_of_diagnosis)
                    : ''
                }
                style={{ width: '100%' }}
                colorTheme={Themes.Primary}
                inputHeight={40}
                disabled={!editable}
                onChange={(e: Moment) =>
                  handleFieldUpdate('date_of_diagnosis', e.format('YYYY-MM-DD'))
                }
                onBlur={() => handleBlur('date_of_diagnosis')}
                error={editable && errors['date_of_diagnosis']}
              />
            </Grid>
            <Grid item xs={2}>
              <Typography fontWeight="bold" mb={1}>
                Referring provider name*
              </Typography>
              <FHInputField
                data-testid="referralProviderName"
                value={referralHistory?.referring_provider_name ?? ''}
                fullWidth
                disabled={!editable}
                colorTheme={Themes.Primary}
                onChange={(e) => handleFieldUpdate('referring_provider_name', e.target.value)}
                onBlur={() => handleBlur('referring_provider_name')}
                inputHeight={40}
                error={editable && errors['referring_provider_name']}
              />
            </Grid>
            <Grid item xs={8}></Grid>
          </Grid>
          <Grid item xs={12} paddingTop={'0px'}>
            <Box sx={{ display: 'flex', alignItems: 'start', flexDirection: 'column' }}>
              {(referralHistory.assessment_tools?.length > 0 || isAddingTool) && (
                <Typography fontWeight="bold" mb={3} marginTop={3}>
                  Assessment tool (s) used
                </Typography>
              )}
              {editable && (
                <FHButton
                  data-testid="addToolButton"
                  disabled={isAddingTool || !referralHistory?.id}
                  colorTheme={Themes.Primary}
                  onClick={handleAddTools}
                  startIcon={<AddIcon sx={{ color: FORTA_WHITE }} />}
                  error={errors['assessment_tools'] && !isAddingTool}
                  sx={{ mb: 2 }}
                >
                  Add Tools
                </FHButton>
              )}
            </Box>

            {referralHistory.assessment_tools?.map((tool: IReferralAssessmentTool) => (
              <ToolRow
                key={tool.id}
                tool={tool}
                onSave={(tool: IReferralAssessmentTool) => handleSaveTool(tool)}
                onRemove={() => handleRemoveTool(tool.id)}
              />
            ))}
            {isAddingTool && (
              <ToolRow
                onSave={(tool: IReferralAssessmentTool) => handleSaveTool(tool)}
                onRemove={() => handleRemoveTool(undefined)}
              />
            )}
          </Grid>

          <Grid item xs={12}>
            <Box style={{ display: 'flex' }} mb={1}>
              <Typography fontWeight={'bold'} mr={1}>
                Reason for referral*
              </Typography>
              <Typography>
                (Please provide specific patient symptoms to be targeted and a need for ABA -
                medical necessity):
              </Typography>
            </Box>
            <FHTextarea
              data-testid="reasonForReferralTextField"
              fullWidth
              colorTheme={Themes.Primary}
              disabled={!editable}
              expandable={editable}
              minLength={MINIMUM_REQUIRED_SENTENCE_LENGTH}
              value={referralHistory?.reason_for_referral ?? ''}
              onEdit={(e: ChangeEvent<HTMLTextAreaElement>) =>
                handleFieldUpdate('reason_for_referral', e.target.value)
              }
              onBlur={() => handleBlur('reason_for_referral')}
              onCloseExpandDialog={() => handleBlur('reason_for_referral')}
              error={errors['reason_for_referral']}
              helperText={
                errors['reason_for_referral']
                  ? `Minimum ${MINIMUM_REQUIRED_SENTENCE_LENGTH} characters`
                  : ''
              }
            />
          </Grid>
        </Grid>
        {referralHistoryLoading && <FProgressBar />}
      </Box>
    </FBoxContainer>
  );
}

export default ReferralHistory;
